import classNames from 'classnames/bind';
import { usePriceTransparency } from 'contexts/priceTransparency';
import {
  PriceTransparencyCancel,
  PriceTransparencyCancelCondition,
  PriceTransparencyChange,
  PriceTransparencyChangeCondition,
} from 'types/apollo';
import RouteInfoWithLogo from './ModalContent/RouteInfoWithLogo';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface CancelChangeProps {
  policyList?: (
    | PriceTransparencyCancel
    | PriceTransparencyChange
    | undefined
    | null
  )[];
  type: 'cancel' | 'change';
}

const formatTime = (period?: number) => {
  if (!period) {
    return { time: 0, unit: '' };
  }
  if (period >= 24 * 60) {
    return { time: period / (24 * 60), unit: '일' };
  }
  if (period >= 60 && !(period % 60)) {
    return { time: period / 60, unit: '시간' };
  }
  return { time: period, unit: '분' };
};

const formatPeriod = (
  type: string,
  currentPeriod: number,
  previousPeriod?: number,
) => {
  const typeCode = type?.[0]?.toUpperCase();
  const [curr, prev] = [formatTime(currentPeriod), formatTime(previousPeriod)];
  if (typeCode === 'N') {
    return '노쇼';
  }
  if (typeCode === 'A') {
    return '출발 이후';
  }
  if (previousPeriod === undefined) {
    return `출발 ${curr.time ? `${curr.time}${curr.unit}` : ''} 이전`;
  }
  if (currentPeriod === 0) {
    return `출발 ${prev.time ? `${prev.time - 1}${prev.unit}` : ''} 이전`;
  }
  return `출발 ${prev.time - 1}${prev.unit} ~ ${curr.time}${curr.unit} 이전`;
};

const formatFee = (
  fee: number,
  currency: string,
  originalTotalFare: number,
) => {
  const isKoreanCurrency =
    currency === 'PER' || currency === 'KRW' || !currency;
  return `${!isKoreanCurrency ? `${currency} ` : ''}${(currency === 'PER'
    ? Math.round(fee * originalTotalFare)
    : fee
  ).toLocaleString()}${isKoreanCurrency ? '원' : ''}`;
};

const renderConditionRows = (
  typeName: string,
  conditions?:
    | PriceTransparencyCancelCondition[]
    | PriceTransparencyChangeCondition[]
    | null,
  originalTotalFare?: number,
) => {
  const filteredCondition = conditions
    ?.slice()
    .sort((a, b) => b.period - a.period)
    .filter(
      condition =>
        (condition.fee || condition.period) &&
        !(condition.type?.[0]?.toUpperCase() === 'A' && condition.period > 0),
    );

  if (!conditions || conditions.length === 0 || !filteredCondition?.length) {
    return (
      <tr>
        <td>-</td>
        <td>확인필요({typeName}가능)</td>
      </tr>
    );
  }

  return filteredCondition.map((condition, index) => {
    const periodText = formatPeriod(
      condition.type,
      condition.period,
      conditions[index - 1]?.period,
    );
    const feeText =
      condition.fee !== -1
        ? formatFee(condition.fee, condition.currency, originalTotalFare ?? 0)
        : `${typeName} 불가`;
    return (
      <tr key={index}>
        <td>{periodText}</td>
        <td>{feeText}</td>
      </tr>
    );
  });
};

const CancelChange = ({ policyList, type }: CancelChangeProps) => {
  const typeName = type === 'cancel' ? '환불/취소' : '일정 변경';
  const { originalTotalFare } = usePriceTransparency();

  return (
    <div className={cx('CancelChange')}>
      <div className={cx('panel')} data-type="panel">
        {policyList?.map((policy, index) => (
          <div key={index} className={cx('section')}>
            <RouteInfoWithLogo routeIndex={index} />
            <table className={cx('table')}>
              <thead>
                <tr>
                  <th>기간 (조건)</th>
                  <th>항공사 패널티 (1인 기준)</th>
                </tr>
              </thead>
              <tbody>
                {policy ? (
                  policy.available ? (
                    renderConditionRows(
                      typeName,
                      policy?.conditions,
                      originalTotalFare,
                    )
                  ) : (
                    <tr>
                      <td>모든 기간</td>
                      <td>{typeName} 불가</td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td>-</td>
                    <td>{typeName} 가능여부 확인필요</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
        <p className={cx('note')}>
          현지 출발 시간 기준, 여행사 수수료 미포함, 성인 기준
        </p>
      </div>
      <div className={cx('footnote')}>
        <p className={cx('text')}>
          본 정보는 해당 항공사의{' '}
          {`${typeName === '일정 변경' ? '항공권 ' : ''}${typeName}`} 규정에
          대한 참고용 안내입니다. 자세한 내용은 항공권 예약 시 여행사
          홈페이지에서 확인 가능하며, 실제 {typeName} 접수는 여행사 고객센터
          운영시간에만 진행될 수 있으니 주의하시기 바랍니다.
        </p>
      </div>
    </div>
  );
};

export default CancelChange;
